import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  useTheme
} from "@mui/material";
import { SettingsOptionWrapper, SettingsSwitchOptionWrapper } from "../../../../components/form/OptionsWrapper";
import { SettingsHead } from "../../../../components/form/SettingsHead";
import React, { useState } from "react";
import { FormButtons } from "../../../../components/form/FormButtons";
import { Project, UpdateProjectData } from "../../../../models/Project";

interface Props {
  project: Project
  submit: (data: UpdateProjectData) => Promise<void>
}

interface State {
  isLoading: boolean
  enabled: boolean
  passcode_charset: "numeric" | "alphanumeric"
  passcode_ttl: number
}

export const Passcode = (props: Props) => {
  const theme = useTheme()
  const { project, submit } = props
  const [state, setState] = useState<State>({
    isLoading: false,
    enabled: project.email.use_for_authentication ?? false,
    passcode_charset: (project.email.passcode_charset || "numeric") as "numeric" | "alphanumeric",
    passcode_ttl: project.email.passcode_ttl,
  })

  const somethingChanged = () => {
    return (project.email.use_for_authentication ?? false) !== state.enabled ||
      ((project.email.passcode_charset || "numeric") as "numeric" | "alphanumeric") !== state.passcode_charset ||
      project.email.passcode_ttl != state.passcode_ttl
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.type === "checkbox") {
      setState({
        ...state,
        [e.target.name]: e.target.checked,
      })
    } else if (e.target.type === "number") {
      setState({
        ...state,
        [e.target.name]: e.target.valueAsNumber,
      })
    } else {
      setState({
        ...state,
        [e.target.name]: e.target.value,
      })
    }
  }

  const onSelectChange = (e: SelectChangeEvent) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    })
  }

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setState({ ...state, isLoading: true })
    await submit({
      email: {
        ...project.email,
        use_for_authentication: state.enabled,
        passcode_charset: state.passcode_charset,
        passcode_ttl: state.passcode_ttl,
      }
    })
    setState({ ...state, isLoading: false })
  }

  const onCancel = () => {
    setState({
      ...state,
      enabled: project.email.use_for_authentication ?? false,
      passcode_charset: (project.email.passcode_charset || "numeric") as "numeric" | "alphanumeric",
      passcode_ttl: project.email.passcode_ttl,
    })
  }

  return <Box>
    <form onSubmit={onSubmit}>
      <SettingsSwitchOptionWrapper sx={{
        borderBottom: `1px solid ${theme.palette.grey["900"]}`
      }}>
        <SettingsHead title="Passcodes"
                      description="Allow users to log in by entering a passcode that was sent to their (primary) email address."
                      helpText="This setting controls the option for users to log in using an email passcode. This does not necessarily mean that an email address must be entered for the login, e.g. if the login identifier is a username to which an email address is assigned."
                      sx={{ mb: 1 }} />
        <Switch name="enabled" color="success" checked={state.enabled}
                onChange={onChange} />
      </SettingsSwitchOptionWrapper>
      <SettingsOptionWrapper>
        <SettingsHead title="Passcode charset"
                      description="The character set used to generate the passcode."
                      size="small"
                      sx={{ mb: 0 }} minElementsVersion="1.0" />
        <FormControl fullWidth sx={{ maxWidth: { xs: '100%', md: '220px' } }} size="small" disabled={!state.enabled}>
          <InputLabel id="passcode_charset">Charset</InputLabel>
          <Select labelId="passcode_charset" id="passcode_charset_select"
                  name="passcode_charset" label="Charset" onChange={onSelectChange}
                  value={state.passcode_charset}>
            <MenuItem key="numeric" value="numeric">Numeric</MenuItem>
            <MenuItem key="alphanumeric" value="alphanumeric">Alphanumeric</MenuItem>
          </Select>
        </FormControl>
      </SettingsOptionWrapper>
      <SettingsOptionWrapper>
        <SettingsHead title="Passcode TTL (seconds)" description="Set the lifetime of a passcode (in seconds)."
                      size="small" sx={{ mb: 0 }} />
        <TextField fullWidth label="Passcode TTL (seconds)" sx={{ maxWidth: { xs: '100%', md: '220px' } }}
                   type="number"
                   inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, max: 3600, step: 1 }}
                   name="passcode_ttl" size="small" onChange={onChange} disabled={!state.enabled}
                   value={state.passcode_ttl} />
      </SettingsOptionWrapper>
      <FormButtons onCancel={onCancel} loading={state.isLoading} disabled={!somethingChanged()} />
    </form>
  </Box>
}

